import React, { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, useNavigate, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from './store/authStore'
import { ThemeProvider } from './components/ui/theme-provider'
import Layout from './components/layout/Layout'
import AgencySetupGuard from './components/auth/AgencySetupGuard'
import Loading from './components/ui/Loading'
import ComingSoon from './pages/ComingSoon'
import SmartRouteHandler from './components/routing/SmartRouteHandler'
import SubdomainRedirect from './components/routing/SubdomainRedirect'
import RedirectToPrivacy from './components/routing/RedirectToPrivacy'
import RedirectToTerms from './components/routing/RedirectToTerms'
import RedirectToCars from './components/routing/RedirectToCars'
import { Toaster } from 'sonner'
import { HelmetProvider } from 'react-helmet-async'
import ScrollToTop from './components/ScrollToTop'
import UrlNormalizer from './components/routing/UrlNormalizer'
import ErrorBoundary from './components/ErrorBoundary'

// Import Home directly (not lazy) since it's the main landing page and LCP element
import Home from './pages/Home'
const Cars = lazy(() => import('./pages/Cars'))
const CarDetails = lazy(() => import('./pages/CarDetails'))
const Agencies = lazy(() => import('./pages/Agencies'))
const AgencyDetails = lazy(() => import('./pages/AgencyDetails'))
const Brands = lazy(() => import('./pages/Brands'))
const BrandDetails = lazy(() => import('./pages/BrandDetails'))
const Cities = lazy(() => import('./pages/Cities'))
const Dashboard = lazy(() => import('./pages/customer/Dashboard'))
const AgencyDashboard = lazy(() => import('./pages/agency/Dashboard'))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminUsers = lazy(() => import('./components/admin/AdminUsers'))
const AdminAgencies = lazy(() => import('./pages/admin/AdminAgencies'))
const AdminCars = lazy(() => import('./pages/admin/AdminCars'))
const AdminClients = lazy(() => import('./pages/admin/AdminClients'))
const AdminBookings = lazy(() => import('./pages/admin/AdminBookings'))
const AdminStats = lazy(() => import('./pages/admin/AdminStats'))
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'))
const AdminPricing = lazy(() => import('./pages/admin/AdminPricing'))
const AdminBrandsModels = lazy(() => import('./pages/admin/AdminBrandsModels'))
const AdminCarImages = lazy(() => import('./pages/admin/AdminCarImages'))
const AdminWhatsAppMessages = lazy(() => import('./pages/admin/AdminWhatsAppMessages'))
const AdminWhatsAppTemplates = lazy(() => import('./pages/admin/AdminWhatsAppTemplates'))
const AdminWhatsAppSettings = lazy(() => import('./pages/admin/AdminWhatsAppSettings'))
const ProfileSettings = lazy(() => import('./pages/ProfileSettings'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const NotFound = lazy(() => import('./pages/NotFound'))
const Login = lazy(() => import('./pages/auth/Login'))
const Signup = lazy(() => import('./pages/auth/Signup'))
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'))
const EmailVerification = lazy(() => import('./pages/auth/EmailVerification'))
const EmailConfirmed = lazy(() => import('./pages/auth/EmailConfirmed'))
const AgencySetup = lazy(() => import('./pages/auth/AgencySetup'))
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'))
const WatermarkTest = lazy(() => import('./pages/WatermarkTest'))

// Test SEO Route Component
const TestSEORoute: React.FC = () => {
  const params = useParams()
  
  // Extract city from parameter
  const city = params.city || 'unknown'
  
  return (
    <div style={{ padding: '20px', fontSize: '18px' }}>
      <h1>SEO Route Test</h1>
      <p>Route is working!</p>
      <p>City parameter: {city}</p>
      <p>All params: {JSON.stringify(params)}</p>
      <p>Current URL: {window.location.pathname}</p>
    </div>
  )
}

// Admin Redirect Component
const AdminRedirect: React.FC = () => {
  const navigate = useNavigate()
  
  useEffect(() => {
    // Get the user's preferred language or default to French
    let preferredLang = 'fr' // Default language
    
    // Check if user has a stored language preference
    const storedLang = localStorage.getItem('i18nextLng')
    if (storedLang && ['en', 'fr', 'ar'].includes(storedLang)) {
      preferredLang = storedLang
    } else {
      // Fallback to browser language if supported
      const browserLang = navigator.language.split('-')[0]
      if (['en', 'fr', 'ar'].includes(browserLang)) {
        preferredLang = browserLang
      }
    }
    
    const redirectUrl = `/${preferredLang}/admin`
    console.log('AdminRedirect: Redirecting to:', redirectUrl)
    navigate(redirectUrl, { replace: true })
  }, [navigate])

  return null
}

// Language Detection and Redirect Component
const LanguageDetector: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  
  useEffect(() => {
    const handleRedirect = async () => {
      // Check if we're already on a language-specific URL
      const currentPath = location.pathname
      const hasLanguagePrefix = /^\/(en|fr|ar)/.test(currentPath)
      
      if (hasLanguagePrefix) {
        // Already on a language-specific URL, no need to redirect
        // This shouldn't happen as language-prefixed routes should be handled by LanguageRouteWrapper
        // But if it does, redirect to the correct route structure
        const pathWithoutLang = currentPath.replace(/^\/(en|fr|ar)/, '')
        if (pathWithoutLang) {
          // For SEO-friendly URLs, they should already be handled by the main routing.
          return
        }
        return
      }
      
      // Check if this might be an agency slug (single path segment without slashes)
      const pathSegments = currentPath.split('/').filter(Boolean)
      if (pathSegments.length === 1) {
        const potentialSlug = pathSegments[0]
        
        // Skip redirect for potential agency slugs - let SmartRouteHandler handle them
        // We'll let the SmartRouteHandler determine if it's a valid agency slug
        console.log('LanguageDetector: Skipping redirect for potential agency slug:', potentialSlug)
        return
      }
      
      // For routes without language prefix, get saved language preference from localStorage
      const savedLanguage = localStorage.getItem('kiracar-language')
      
      // Default to French if no saved preference
      const defaultLanguage = savedLanguage || 'fr'
      
      // Redirect to the appropriate language version
      const newPath = `/${defaultLanguage}${currentPath === '/' ? '' : currentPath}`
      navigate(newPath, { replace: true })
    }
    
    handleRedirect()
  }, [navigate, location])

  // If we reach here, we're either redirecting or handling a language-prefixed URL that fell through
  // In the latter case, we should return null to avoid infinite loading
  return null
}

// Language Route Wrapper Component
const LanguageRouteWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const params = useParams()
  const { lang } = params as { lang: string }
  const { i18n } = useTranslation()
  

  useEffect(() => {
    if (lang && ['en', 'fr', 'ar'].includes(lang)) {
      
      // PRIORITY: URL language always takes precedence - FORCE change
      
      // Force change language to match URL (even if already same)
      i18n.changeLanguage(lang)
      
      // Update saved language preference to match URL language
      localStorage.setItem('kiracar-language', lang)
      
      // Manually update document attributes immediately
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr'
      document.documentElement.lang = lang
      
      // Force re-render by updating body class for debugging
      document.body.setAttribute('data-lang', lang)
      
    }
  }, [lang, i18n])

  return <>{children}</>
}

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: 'customer' | 'agency' | 'admin'
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, profile, initialized } = useAuthStore()
  const { lang } = useParams<{ lang: string }>()
  const currentLang = lang || 'fr'
  const [authTimeout, setAuthTimeout] = React.useState(false)

  // Set a timeout to prevent infinite loading
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (!initialized) {
        console.warn('Auth initialization timeout - redirecting to login')
        setAuthTimeout(true)
      }
    }, 10000) // 10 second timeout for production environments

    return () => clearTimeout(timer)
  }, [initialized])

  // If auth initialization times out, redirect to login
  if (authTimeout && !initialized) {
    return <Navigate to={`/${currentLang}/login`} replace />
  }

  // Show loading while auth is initializing (with timeout protection)
  if (!initialized) {
    return (
      <Loading 
        size="md" 
        fullScreen={true}
      />
    )
  }

  // After initialization, check authentication
  if (!user) {
    return <Navigate to={`/${currentLang}/login`} replace />
  }

  if (!profile) {
    // User exists but no profile - this is an edge case
    return <Navigate to={`/${currentLang}/login`} replace />
  }

  if (requiredRole && profile.role !== requiredRole) {
    return <Navigate to={`/${currentLang}/unauthorized`} replace />
  }

  // User is authenticated and authorized, render the children
  return <>{children}</>
}

// Coming Soon Wrapper Component
const ComingSoonWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isComingSoonEnabled = import.meta.env.VITE_COMING_SOON_ENABLED === 'true'
  const location = useLocation()
  
  // Extract language and path from the current location
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const routePath = pathSegments.slice(1).join('/') // Rest of the path after language
  
  // Only show coming soon on the home page (root path after language)
  const isHomePage = routePath === '' || routePath === '/'
  
  // Debug logging (only in development)
  if (import.meta.env.DEV && isComingSoonEnabled) {
    // Debug info for coming soon page logic
  }
  
  // Only show coming soon page if enabled AND we're on the home page
  if (isComingSoonEnabled && isHomePage) {
    return <ComingSoon />
  }
  
  return <>{children}</>
}

function App() {
  const { i18n } = useTranslation()
  const { initialize } = useAuthStore()

  useEffect(() => {
    // Initialize authentication in the background (non-blocking)
    const initializeAuth = async () => {
      try {
        await initialize()
      } catch (error) {
        console.error('Failed to initialize auth:', error)
      }
    }

    // Start auth initialization in background
    initializeAuth()

    // Initialize default language preference only if none exists
    // This will be overridden by URL language via LanguageRouteWrapper
    const savedLanguage = localStorage.getItem('kiracar-language')
    
    if (savedLanguage && ['en', 'fr', 'ar'].includes(savedLanguage)) {
      i18n.changeLanguage(savedLanguage)
    } else {
      // Default to French for first-time visitors
      i18n.changeLanguage('fr')
      localStorage.setItem('kiracar-language', 'fr')
    }

    // Set document direction based on language
    const updateDirection = () => {
      document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr'
      document.documentElement.lang = i18n.language
    }

    updateDirection()
    i18n.on('languageChanged', updateDirection)

    return () => {
      i18n.off('languageChanged', updateDirection)
    }
  }, [initialize, i18n])

  // No loading screen - render immediately

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <Router>
            <ScrollToTop />
            <UrlNormalizer />
            <Toaster />
            <ComingSoonWrapper>
            <div className="App">
              <Routes>
              {/* Subdomain redirect handler - must be first to catch subdomain requests */}
              <Route path="/" element={<SubdomainRedirect />} />
              
              {/* Category routes are now handled inside the /:lang structure below */}
              
              {/* Language-based Routes with Layout (Header + Footer) */}
              <Route path="/:lang" element={<LanguageRouteWrapper><Layout /></LanguageRouteWrapper>}>
                <Route index element={<Home />} />
                <Route path="cars" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Cars />
                  </Suspense>
                } />
                
                {/* CATEGORY ROUTES - Fixed with Simple Parameter Pattern */}
                <Route path="category/:categoryPattern" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Cars />
                  </Suspense>
                } />
                
                {/* CAR DETAIL ROUTES - CLEAN & CONFLICT-FREE */}
                <Route path="cars/location-voiture-:slug" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <CarDetails />
                  </Suspense>
                } /> {/* French enhanced SEO */}
                <Route path="cars/rent-car-:slug" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <CarDetails />
                  </Suspense>
                } /> {/* English enhanced SEO */}
                <Route path="cars/تأجير-سيارة-:slug" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <CarDetails />
                  </Suspense>
                } /> {/* Arabic enhanced SEO */}
                
                {/* FALLBACK CAR ROUTES */}
                <Route path="cars/:slug" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <CarDetails />
                  </Suspense>
                } /> {/* Generic car slugs */}
                <Route path="cars/:id" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <CarDetails />
                  </Suspense>
                } /> {/* Legacy UUIDs */}
                
                {/* SEO-friendly location-based routes - cleaner URL format */}
                <Route path="location-voiture/:city" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Cars />
                  </Suspense>
                } />
                <Route path="rent-car/:city" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Cars />
                  </Suspense>
                } />
                <Route path="تأجير-سيارة/:city" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Cars />
                  </Suspense>
                } />
                <Route path="testseo/:city" element={<TestSEORoute />} />
                
                {/* SEO-friendly location-based car detail routes */}
                <Route path="location-voiture/:city/:slug" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <CarDetails />
                  </Suspense>
                } />
                <Route path="rent-car/:city/:slug" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <CarDetails />
                  </Suspense>
                } />
                <Route path="تأجير-سيارة/:city/:slug" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <CarDetails />
                  </Suspense>
                } />
                <Route path="agencies" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Agencies />
                  </Suspense>
                } />
                <Route path="agencies/:slug" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <AgencyDetails />
                  </Suspense>
                } />
                <Route path="agencies/:id" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <AgencyDetails />
                  </Suspense>
                } /> {/* Legacy UUID support */}
                
                {/* Direct agency slug routes (e.g., /BrahimCars) */}
                <Route path=":slug" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <AgencyDetails />
                  </Suspense>
                } />
                <Route path="brands" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Brands />
                  </Suspense>
                } />
                <Route path="brands/:slug" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <BrandDetails />
                  </Suspense>
                } />
                <Route path="cities" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Cities />
                  </Suspense>
                } />
                
                {/* Customer Dashboard Routes */}
                <Route path="dashboard" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Dashboard />
                  </Suspense>
                } />
                
                {/* Profile Settings - Available to all authenticated users */}
                <Route path="profile" element={
                  <ProtectedRoute>
                    <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                      <ProfileSettings />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="profile-settings" element={
                  <ProtectedRoute>
                    <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                      <ProfileSettings />
                    </Suspense>
                  </ProtectedRoute>
                } />
                
                {/* Static Pages */}
                <Route path="about" element={<div>About Page</div>} />
                <Route path="contact" element={<div>Contact Page</div>} />
                <Route path="watermark-test" element={
                  <Suspense fallback={<Loading />}>
                    <WatermarkTest />
                  </Suspense>
                } />
                {/* Redirect old privacy-policy URL to privacy */}
                <Route path="privacy-policy" element={<RedirectToPrivacy />} />
                <Route path="privacy" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Privacy />
                  </Suspense>
                } />
                {/* Redirect old terms-of-service URL to terms */}
                <Route path="terms-of-service" element={<RedirectToTerms />} />
                <Route path="terms" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Terms />
                  </Suspense>
                } />
                {/* Redirect categories to cars (categories are now handled via filters) */}
                <Route path="categories" element={<RedirectToCars />} />
                <Route path="unauthorized" element={<div>Unauthorized Access</div>} />
                {/* 404 Not Found */}
                <Route path="404" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <NotFound />
                  </Suspense>
                } />
                {/* Catch all route for language-prefixed URLs */}
                <Route path="*" element={
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <NotFound />
                  </Suspense>
                } />
              </Route>

              {/* Agency Dashboard Routes (No Layout - No Header/Footer) */}
              <Route path="/:lang/agency" element={<LanguageRouteWrapper><Outlet /></LanguageRouteWrapper>}>
                <Route path="dashboard" element={
                  <AgencySetupGuard>
                    <Suspense fallback={<Loading size="md" />}>
                      <AgencyDashboard />
                    </Suspense>
                  </AgencySetupGuard>
                } />
                <Route path="cars" element={
                  <AgencySetupGuard>
                    <Suspense fallback={<Loading size="md" />}>
                      <AgencyDashboard />
                    </Suspense>
                  </AgencySetupGuard>
                } />
                <Route path="clients" element={
                  <AgencySetupGuard>
                    <Suspense fallback={<Loading size="md" />}>
                      <AgencyDashboard />
                    </Suspense>
                  </AgencySetupGuard>
                } />
                <Route path="bookings" element={
                  <AgencySetupGuard>
                    <Suspense fallback={<Loading size="md" />}>
                      <AgencyDashboard />
                    </Suspense>
                  </AgencySetupGuard>
                } />
                <Route path="calendar" element={
                  <AgencySetupGuard>
                    <Suspense fallback={<Loading size="md" />}>
                      <AgencyDashboard />
                    </Suspense>
                  </AgencySetupGuard>
                } />
                <Route path="contracts" element={
                  <AgencySetupGuard>
                    <Suspense fallback={<Loading size="md" />}>
                      <AgencyDashboard />
                    </Suspense>
                  </AgencySetupGuard>
                } />
                <Route path="invoices" element={
                  <AgencySetupGuard>
                    <Suspense fallback={<Loading size="md" />}>
                      <AgencyDashboard />
                    </Suspense>
                  </AgencySetupGuard>
                } />
                <Route path="reports" element={
                  <AgencySetupGuard>
                    <Suspense fallback={<Loading size="md" />}>
                      <AgencyDashboard />
                    </Suspense>
                  </AgencySetupGuard>
                } />
                <Route path="settings" element={
                  <AgencySetupGuard>
                    <Suspense fallback={<Loading size="md" />}>
                      <AgencyDashboard />
                    </Suspense>
                  </AgencySetupGuard>
                } />
              </Route>
              
              {/* Admin Dashboard Routes (No Layout - No Header/Footer) */}
              <Route path="/:lang/admin" element={
                <LanguageRouteWrapper>
                  <ProtectedRoute requiredRole="admin">
                    <Outlet />
                  </ProtectedRoute>
                </LanguageRouteWrapper>
              }>
                <Route index element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminDashboard />
                  </Suspense>
                } />
                <Route path="agencies" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminAgencies />
                  </Suspense>
                } />
                <Route path="cars" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminCars />
                  </Suspense>
                } />
                <Route path="clients" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminClients />
                  </Suspense>
                } />
                <Route path="bookings" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminBookings />
                  </Suspense>
                } />
                <Route path="stats" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminStats />
                  </Suspense>
                } />
                <Route path="users" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminUsers />
                  </Suspense>
                } />
                <Route path="settings" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminSettings />
                  </Suspense>
                } />
                <Route path="pricing" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminPricing />
                  </Suspense>
                } />
                <Route path="car-images" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminCarImages />
                  </Suspense>
                } />
                <Route path="brands-models" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminBrandsModels />
                  </Suspense>
                } />
                <Route path="whatsapp/messages" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminWhatsAppMessages />
                  </Suspense>
                } />
                <Route path="whatsapp/templates" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminWhatsAppTemplates />
                  </Suspense>
                } />
                <Route path="whatsapp/settings" element={
                  <Suspense fallback={<Loading size="md" />}>
                    <AdminWhatsAppSettings />
                  </Suspense>
                } />
                <Route path="help" element={<div>Admin Help</div>} />
              </Route>

              {/* Default Routes (detect language and redirect) */}
              <Route path="/cars" element={<LanguageDetector />} />
              <Route path="/agencies" element={<LanguageDetector />} />
              <Route path="/about" element={<LanguageDetector />} />
              <Route path="/contact" element={<LanguageDetector />} />
              <Route path="/dashboard" element={<LanguageDetector />} />
              <Route path="/profile" element={<LanguageDetector />} />
              <Route path="/profile-settings" element={<LanguageDetector />} />
              <Route path="/agency/*" element={<LanguageDetector />} />
              <Route path="/admin" element={<AdminRedirect />} />

              {/* Auth Routes with Language Support */}
              <Route path="/:lang/login" element={
                <LanguageRouteWrapper>
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Login />
                  </Suspense>
                </LanguageRouteWrapper>
              } />
              <Route path="/:lang/signup" element={
                <LanguageRouteWrapper>
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <Signup />
                  </Suspense>
                </LanguageRouteWrapper>
              } />
              <Route path="/:lang/forgot-password" element={
                <LanguageRouteWrapper>
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <ForgotPassword />
                  </Suspense>
                </LanguageRouteWrapper>
              } />
              <Route path="/:lang/reset-password" element={
                <LanguageRouteWrapper>
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <ResetPassword />
                  </Suspense>
                </LanguageRouteWrapper>
              } />
              <Route path="/:lang/email-verification" element={
                <LanguageRouteWrapper>
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <EmailVerification />
                  </Suspense>
                </LanguageRouteWrapper>
              } />
              <Route path="/:lang/email-confirmed" element={
                <LanguageRouteWrapper>
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <EmailConfirmed />
                  </Suspense>
                </LanguageRouteWrapper>
              } />
              <Route path="/:lang/agency-setup" element={
                <LanguageRouteWrapper>
                  <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                    <AgencySetup />
                  </Suspense>
                </LanguageRouteWrapper>
              } />
              <Route path="/reset-password" element={
                <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                  <ResetPassword />
                </Suspense>
              } />
              
              {/* OAuth Callback Route (no language prefix needed) */}
              <Route path="/auth/callback" element={
                <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                  <AuthCallback />
                </Suspense>
              } />
              
              {/* Auth Routes Redirects */}
              <Route path="/login" element={<LanguageDetector />} />
              <Route path="/signup" element={<LanguageDetector />} />
              <Route path="/forgot-password" element={<LanguageDetector />} />
              <Route path="/email-verification" element={<LanguageDetector />} />
              <Route path="/email-confirmed" element={<LanguageDetector />} />
              <Route path="/agency-setup" element={<LanguageDetector />} />

              {/* Smart route handler for root-level agency slugs (e.g., /brahimcars) */}
              <Route path="/:slug" element={<SmartRouteHandler />} />

              {/* Catch all route - redirect to 404 */}
              <Route path="*" element={
                <Suspense fallback={<Loading size="md" fullScreen={true} />}>
                  <NotFound />
                </Suspense>
              } />
              </Routes>
            </div>
          </ComingSoonWrapper>
          </Router>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  )
}

export default App
